import { Link } from 'react-router';
import { lazy, Suspense, useState } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { Order, RideInvoiceOutcome, RideLog } from '@kontor/shared';
import { api, ApiError } from '../../api';
import { useSession } from '../../session';
import { CollapsibleCard } from '../../components/CollapsibleCard';

const RideMap = lazy(() => import('./RideMap'));
const stamp = new Intl.DateTimeFormat('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric', hour: '2-digit', minute: '2-digit', second: '2-digit', timeZone: 'Europe/Berlin' });
const clock = new Intl.DateTimeFormat('de-DE', { hour: '2-digit', minute: '2-digit', second: '2-digit', timeZone: 'Europe/Berlin' });
/** Karte mit der Position, die Adresse nennt Koordinaten und einen Marker */
const mapUrl = (lat: number, lon: number) => `https://www.openstreetmap.org/?mlat=${lat}&mlon=${lon}#map=17/${lat}/${lon}`;
const coords = (lat: number, lon: number) => `${lat.toFixed(5)}, ${lon.toFixed(5)}`;
const duration = (from: string, to: string) => {
  const minutes = Math.round((new Date(to).getTime() - new Date(from).getTime()) / 60000);
  return minutes < 60 ? `${minutes} Min.` : `${Math.floor(minutes / 60)} Std. ${minutes % 60} Min.`;
};

/**
 * Fahrtprotokoll (KON-245): Der Fahrer setzt die Schritte der Fahrt in der App selbst (Start, Angekommen, Fahrgast angetroffen, Fahrgast im Fahrzeug, Abgeschlossen). Hier stehen sie mit Zeit und der
 * Position des Handys, dazu die Positionen während der Fahrt und die Strecke. Das Protokoll dient bei Streitfällen als Nachweis und ist nur im Backoffice zu sehen, nicht in der App. Es erscheint, sobald
 * ein Schritt gesetzt ist.
 */
export function RideLogPanel({ order }: { order: Order }) {
  const { can, session } = useSession();
  const allowed = session?.tenant.industry === 'chauffeur' && can('orders.write');
  const log = useQuery({ queryKey: ['ride-log', order.id, order.updatedAt], queryFn: async () => (await api<{ data: RideLog }>('GET', `/orders/${order.id}/ride-log`)).data, enabled: allowed });
  const d = log.data;
  const client = useQueryClient();
  const [retryError, setRetryError] = useState<string | null>(null);
  // Rechnung noch einmal versuchen, nachdem fehlende Angaben ergänzt wurden (Anschrift des Kunden, Firmendaten)
  const retry = useMutation({
    mutationFn: async () => (await api<{ data: RideInvoiceOutcome }>('POST', `/orders/${order.id}/invoice-auto`)).data,
    onSuccess: () => {
      setRetryError(null);
      void client.invalidateQueries({ queryKey: ['ride-log', order.id] });
      void client.invalidateQueries({ queryKey: ['order', order.id] });
    },
    onError: (e) => setRetryError(e instanceof ApiError ? e.message : 'Das hat nicht geklappt.'),
  });
  if (!allowed || !d || (d.events.length === 0 && d.trackCount === 0)) return null;
  const first = d.events.find((e) => e.step === 'started');
  const last = d.events.find((e) => e.step === 'completed');

  return (
    <CollapsibleCard title="Fahrtprotokoll" storageKey="order-ride-log" defaultOpen>
      <p className="muted small">Vom Fahrer in der App gesetzt, mit der Position seines Handys zum Zeitpunkt des Schritts. Nur hier im Backoffice sichtbar, als Nachweis bei Rückfragen und Streitfällen.</p>
      {(d.track.length > 0 || d.events.some((e) => e.latitude !== null)) && (
        <Suspense fallback={<div className="ride-map ride-map--loading muted small">Karte wird geladen …</div>}>
          <RideMap log={d} />
        </Suspense>
      )}
      <div className="table-scroll">
        <table className="table table--cards">
          <thead>
            <tr><th>Schritt</th><th>Zeit</th><th>Fahrer</th><th>Position</th></tr>
          </thead>
          <tbody>
            {d.events.map((e) => (
              <tr key={e.id}>
                <td>{e.label}</td>
                <td data-label="Zeit">
                  {stamp.format(new Date(e.recordedAt))} Uhr
                  {e.deviceTime && Math.abs(new Date(e.deviceTime).getTime() - new Date(e.recordedAt).getTime()) > 120_000 && <div className="muted small">Handy: {stamp.format(new Date(e.deviceTime))} Uhr</div>}
                </td>
                <td data-label="Fahrer">{e.driverName ?? '–'}</td>
                <td data-label="Position">
                  {e.latitude !== null && e.longitude !== null ? (
                    <>
                      <a href={mapUrl(e.latitude, e.longitude)} target="_blank" rel="noreferrer noopener">{coords(e.latitude, e.longitude)}</a>
                      {e.accuracyM !== null && <span className="muted small"> (±{Math.round(e.accuracyM)} m)</span>}
                    </>
                  ) : (
                    <span className="muted">ohne Position</span>
                  )}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
      <dl className="inv-facts">
        {first && last && <><dt>Dauer der Fahrt</dt><dd>{duration(first.recordedAt, last.recordedAt)} (Start bis Abschluss)</dd></>}
        {first?.odometerKm != null && <><dt>Kilometerstand beim Start</dt><dd>{first.odometerKm.toLocaleString('de-DE')} km (vom Fahrer am Tacho bestätigt)</dd></>}
        {d.distanceKm !== null && <><dt>Strecke laut Positionen</dt><dd>{d.distanceKm.toLocaleString('de-DE')} km{d.route ? ' (auf den Straßen berechnet)' : ' (Luftlinie zwischen den Punkten)'}</dd></>}
        {d.route?.expectedKm != null && (
          <>
            <dt>Route laut Auftrag</dt>
            <dd>
              {d.route.expectedKm.toLocaleString('de-DE')} km von Abholort zu Ziel
              {Math.abs(d.route.distanceKm - d.route.expectedKm) > Math.max(2, d.route.expectedKm * 0.25) && <> <span className="badge badge--rejected">Weicht ab</span> Bitte prüfen, ob Umweg, Anfahrt oder fehlende Positionen der Grund sind.</>}
            </dd>
          </>
        )}
        {d.route?.imageFileId && <><dt>Kartenbild</dt><dd><a href={`/api/v1/orders/${order.id}/files/${d.route.imageFileId}/download`} target="_blank" rel="noreferrer noopener">Fahrtroute.png</a> (liegt auch unter den Dateien des Auftrags)</dd></>}
        {(d.logbook || d.logbookNote) && (
          <>
            <dt>Fahrtenbuch</dt>
            <dd>
              {d.logbook ? <>Eintrag im Fahrtenbuch von {d.logbook.plate}: {d.logbook.startKm.toLocaleString('de-DE')} → {d.logbook.endKm.toLocaleString('de-DE')} km ({d.logbook.distanceKm.toLocaleString('de-DE')} km).</> : d.logbookNote}
            </dd>
          </>
        )}
        {d.invoice && (
          <>
            <dt>Rechnung nach Abschluss</dt>
            <dd>
              <span className={`badge ${d.invoice.status === 'sent' ? 'badge--won' : d.invoice.status === 'failed' ? 'badge--rejected' : ''}`}>
                {d.invoice.status === 'sent' ? 'Gesendet' : d.invoice.status === 'issued' ? 'Ausgestellt, nicht gesendet' : d.invoice.status === 'failed' ? 'Nicht erstellt' : 'Keine Rechnung'}
              </span>{' '}
              {d.invoice.message}
              {d.invoice.invoiceId && <> <Link to={`/rechnungen/${d.invoice.invoiceId}`}>Zur Rechnung</Link></>}
              {d.invoice.status === 'failed' && can('orders.write') && order.status === 'done' && (
                <div>
                  <button type="button" className="btn btn--secondary btn--sm" onClick={() => retry.mutate()} disabled={retry.isPending}>{retry.isPending ? 'Wird erstellt …' : 'Rechnung erneut erstellen und senden'}</button>
                  {retryError && <div className="alert alert--error" role="alert">{retryError}</div>}
                </div>
              )}
            </dd>
          </>
        )}
        {d.rating && (
          <>
            <dt>Bewertung des Fahrers</dt>
            <dd>{'★'.repeat(d.rating.stars)}{'☆'.repeat(5 - d.rating.stars)} ({d.rating.stars} von 5, {d.rating.source === 'customer_app' ? 'vom Fahrgast' : 'vom Büro erfasst'}){d.rating.comment ? `: „${d.rating.comment}“` : ''}</dd>
          </>
        )}
        <dt>Positionen unterwegs</dt>
        <dd>{d.trackCount === 0 ? 'keine gespeichert' : `${d.trackCount}${d.distanceKm !== null ? `, zurückgelegte Strecke etwa ${d.distanceKm.toLocaleString('de-DE')} km${d.route ? '' : ' (Luftlinie zwischen den Punkten)'}` : ''}`}</dd>
      </dl>
      {d.track.length > 0 && (
        <details>
          <summary>Alle Positionen anzeigen ({d.track.length}{d.trackCount > d.track.length ? ` von ${d.trackCount}` : ''})</summary>
          <div className="table-scroll">
            <table className="table">
              <thead>
                <tr><th>Uhrzeit</th><th>Position</th><th>Genauigkeit</th><th>Tempo</th></tr>
              </thead>
              <tbody>
                {d.track.map((p, i) => (
                  <tr key={i}>
                    <td>{clock.format(new Date(p.recordedAt))}</td>
                    <td><a href={mapUrl(p.latitude, p.longitude)} target="_blank" rel="noreferrer noopener">{coords(p.latitude, p.longitude)}</a></td>
                    <td>{p.accuracyM !== null ? `±${Math.round(p.accuracyM)} m` : '–'}</td>
                    <td>{p.speedMs !== null ? `${Math.round(p.speedMs * 3.6)} km/h` : '–'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </details>
      )}
    </CollapsibleCard>
  );
}
